import { forwardRef, ReactNode, useMemo } from 'react';
import { css, type SerializedStyles } from '@emotion/react';
import { CallIntelNewFeatureAnnouncement } from '@frontend/call-intel-assets';
import { AnimatedBadge } from '@frontend/components';
import { breakpoints, useTargetElementContainerQuery } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import { Button, Text } from '@frontend/design-system';

type AnnouncementBannerUIProps = {
  badgeLabel: string;
  title: string;
  description: ReactNode;
  primaryActionLabel: string;
  onPrimaryAction: () => void;
  dismissActionLabel?: string;
  onDismissAction?: () => void;
  isMobile?: boolean;
  containerStyles?: SerializedStyles;
  tracking?: {
    primaryActionTrackingId?: string;
    dismissActionTrackingId?: string;
  };
};

const MAIN_CONTENT_SELECTOR = '#main-content';

export const AnnouncementBannerUI = forwardRef<HTMLDivElement, AnnouncementBannerUIProps>(
  (
    {
      badgeLabel,
      title,
      description,
      primaryActionLabel,
      onPrimaryAction,
      dismissActionLabel,
      onDismissAction,
      containerStyles,
      tracking,
    },
    ref
  ) => {
    const mainContentElement = useMemo(() => document.querySelector(MAIN_CONTENT_SELECTOR) as HTMLElement, []);

    const [isMobile] = useTargetElementContainerQuery({
      maxWidth: breakpoints.small.max,
      element: mainContentElement,
    });

    return (
      <div ref={ref} css={[styles.container, containerStyles]}>
        <div css={styles.illustration}>
          <CallIntelNewFeatureAnnouncement width={120} height={68} css={styles.announcementImage} />
        </div>

        <div css={styles.content}>
          <div css={styles.header}>
            <AnimatedBadge label={badgeLabel} />
            <Text as='span' weight='bold' css={styles.title}>
              {title}
            </Text>
          </div>

          <Text size='medium' css={styles.description}>
            {description}
          </Text>
        </div>

        <div css={styles.actions}>
          <Button onClick={onPrimaryAction} trackingId={tracking?.primaryActionTrackingId}>
            {primaryActionLabel}
          </Button>

          {onDismissAction &&
            (isMobile ? (
              <Button
                variant='tertiary'
                onClick={onDismissAction}
                css={styles.mobileDismissButton}
                trackingId={tracking?.dismissActionTrackingId}
              >
                {dismissActionLabel}
              </Button>
            ) : (
              <Button
                iconName='x'
                onClick={onDismissAction}
                variant='secondary'
                trackingId={tracking?.dismissActionTrackingId}
              />
            ))}
        </div>
      </div>
    );
  }
);

AnnouncementBannerUI.displayName = 'AnnouncementBannerUI';

const styles = {
  container: css`
    align-items: center;
    background: ${theme.colorsV2.seaweed50(10)};
    border: 1px solid ${theme.colors.secondary.seaweed50};
    border-radius: ${theme.borderRadius.medium};
    box-shadow: ${theme.shadows.light};
    display: flex;
    gap: ${theme.spacing(2)};
    padding: ${theme.spacing(1.5)};

    @container (max-width: ${breakpoints.small.max}px) {
      align-items: flex-start;
      flex-direction: column;
      padding: ${theme.spacing(2)};
    }
  `,

  illustration: css`
    flex: 0 0 auto;
    width: ${theme.spacing(15)};

    @container (max-width: ${breakpoints.small.max}px) {
      display: none;
    }
  `,

  announcementImage: css`
    display: block;
    height: auto;
    width: ${theme.spacing(15)};
  `,

  content: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(0.5)};
  `,

  header: css`
    align-items: center;
    display: inline-flex;
    gap: ${theme.spacing(1)};

    @container (max-width: ${breakpoints.small.max}px) {
      align-items: flex-start;
      flex-direction: column;
    }
  `,

  title: css`
    @container (max-width: ${breakpoints.small.max}px) {
      font-size: ${theme.font.size.medium};
    }
  `,

  description: css`
    @container (max-width: ${breakpoints.small.max}px) {
      font-size: ${theme.font.size.small};
    }
  `,

  actions: css`
    align-items: center;
    display: flex;
    gap: ${theme.spacing(2)};
    margin-left: auto;

    @container (max-width: ${breakpoints.small.max}px) {
      align-items: flex-start;
      margin-left: 0;
      width: 100%;
    }
  `,

  mobileDismissButton: css`
    color: ${theme.colorsV2.black(60)};

    &:active {
      color: ${theme.colorsV2.black(60)};
    }

    &:focus-visible {
      box-shadow: none;
    }
  `,
};
